import React from "react";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import TextButton from "../ui/TextButton";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { LazyMotion, domAnimation, m } from "framer-motion";
import ResponsiveImage from "../ui/ResponsiveImage";

interface BackgroundImageTextProps {
  data: BackgroundImageTextData;
}

export interface BackgroundImageTextData {
  sectionName?: string;
  smallTitle: string;
  title: string;
  text: string;
  textAligment: "left" | "center" | "right";
  image: any[];
  image_link: any[];
  textBoxStyle: "dark" | "light";
  buttonText?: string;
  buttonLink?: string;
  letter_spacing?: boolean;
}

const backgroundTextBoxVariants = cva("relative w-full desktop:max-w-[50%]", {
  variants: {
    alignment: {
      left: ["w-full"],
      center: ["w-full", "desktop:left-1/2", "desktop:-translate-x-1/2"],
      right: ["w-full", "desktop:ml-auto", "desktop:mr-0"],
    },
  },
  defaultVariants: {
    alignment: "left",
  },
});

const backgroundTextBoxStyleVariants = cva(
  "py-12 px-6 flex flex-col gap-6 md:gap-12 md:py-16 desktop:py-20 px-[7.5%]",
  {
    variants: {
      style: {
        dark: "bg-accent-color-three",
        light: "bg-accent-color-three-light",
      },
    },
    defaultVariants: {
      style: "dark",
    },
  }
);

const textSmallTitleVariants = cva("font-sans text-special-h2 uppercase", {
  variants: {
    style: {
      dark: "text-secondary-light",
      light: "text-[#FFFFFF]",
    },
  },
  defaultVariants: {
    style: "dark",
  },
});

const textTitleVariants = cva(
  "font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop",
  {
    variants: {
      style: {
        dark: "text-accent-color-two-light",
        light: "text-[#FFFFFF]",
      },
    },
    defaultVariants: {
      style: "dark",
    },
  }
);

const textContentVariants = cva(
  ["font-sans font-light whitespace-pre-line", "text-paragraph-small", ""],

  {
    variants: {
      style: {
        dark: "text-[#FAFAFA]",
        light: "text-[#FFFFFF]",
      },
    },
    defaultVariants: {
      style: "dark",
    },
  }
);

const containerTextBox = cva("w-full", {
  variants: {
    alignment: {
      left: "desktop:mr-auto",
      center: "desktop:mx-auto",
      right: "desktop:ml-auto",
    },
  },
  defaultVariants: {
    alignment: "left",
  },
});

export default function BackgroundImageText({
  data,
}: BackgroundImageTextProps) {
  const sectionId = data.sectionName && data.sectionName.replace(/\s+/g, "-");

  const useCloudinary = data.image_link.length;

  const desktopImage = useCloudinary
    ? data.image_link[0].item.desktop_link
    : data.image[0].item.desktop_image;
  const tabletImage = useCloudinary
    ? data.image_link[0].item.tablet_link
    : data.image[0].item.tablet_image;
  const mobileImage = useCloudinary
    ? data.image_link[0].item.mobile_link
    : data.image[0].item.mobile_image;
  const alt = useCloudinary
    ? data.image_link[0].item.alt ?? data.title
    : data.image[0].item.imageAlt ?? data.title;

  return (
    <LazyMotion features={domAnimation}>
      <section
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      className="relative w-full grid auto-rows-min"
      style={{
        display: "grid",
        gridTemplateAreas: "'stack'",
      }}
      data-name={data.sectionName}
    >
      <div
        className="relative h-[50vh] desktop:static desktop:h-auto col-start-1 row-start-1 w-full"
        style={{ gridArea: "stack" }}
      >
        {data.image.length > 0 && (
          <ResponsiveImage
            desktop_image={desktopImage}
            tablet_image={tabletImage}
            mobile_image={mobileImage}
            alt={alt}
            shouldFetch={!useCloudinary}
            className="z-0 w-full object-cover"
          />
        )}
      </div>

      <div
        className={cn(
          containerTextBox({ alignment: data.textAligment }),
          "desktop:max-w-[85vw] desktop:mx-[7.5vw]"
        )}
      >
        <m.div
          className={cn(
            backgroundTextBoxVariants({ alignment: data.textAligment }),
            "col-start-1 row-start-1 w-full"
          )}
          style={{ gridArea: "stack", zIndex: 2 }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div
            className={cn(
              backgroundTextBoxStyleVariants({ style: data.textBoxStyle }),
              "desktop:mt-32 desktop:mb-32"
            )}
          >
            <div className="gap-2 flex flex-col">
              <m.p
                className={cn(
                  textSmallTitleVariants({ style: data.textBoxStyle })
                )}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                {data.smallTitle}
              </m.p>
              <m.h2
                className={cn(textTitleVariants({ style: data.textBoxStyle }))}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                {data.title}
              </m.h2>
            </div>

            <m.div
              className={cn(
                textContentVariants({ style: data.textBoxStyle }),
                data.letter_spacing ? "tracking-normal" : "",
                "[&_*:is(a)]:underline",
                "[&_*:is(ul)]:list-disc [&_*:is(ul)]:pl-6",
                "[&_*:is(ol)]:list-decimal [&_*:is(ol)]:pl-6"
              )}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              dangerouslySetInnerHTML={{ __html: data.text ?? "" }}
            />
            {data.buttonText && data.buttonLink && (
              <m.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                <TextButton
                  text={data.buttonText}
                  link={data.buttonLink}
                  textColor="white"
                />
              </m.div>
            )}
          </div>
        </m.div>
      </div>
    </section>
    </LazyMotion>
  );
}
